/**
 * FilterBuilder — Painel de busca avançada (query builder visual).
 * Inspirado em Jira/Delphi: campos principais visíveis + ocultos via "+ Filtro".
 * Suporta undo/redo, operadores por tipo, e lookup recursivo.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { FaPlus, FaSearch, FaUndo, FaRedo, FaTimes, FaChevronDown, FaChevronUp, FaFilter } from 'react-icons/fa';
import { FilterSchema, AppliedFilter, Operator, operatorLabels } from './types';
import { useFilterHistory } from './useFilterHistory';
import { describeFilter } from './serialize';
import FilterField from './FilterField';

interface Props {
    schema: FilterSchema[];
    onSearch: (filters: AppliedFilter[]) => void;
    loading?: boolean;
    className?: string;
    /** Permite recolher o painel para liberar espaço depois de filtrar (default: true). */
    collapsible?: boolean;
    /** Começa recolhido. */
    defaultCollapsed?: boolean;
    /** Rótulo do cabeçalho quando colapsável. */
    title?: string;
    /**
     * Filtros já aplicados ao montar — para deep-link (outra tela navega para cá com filtro).
     * NÃO dispara onSearch: quem navega já semeia o próprio estado, senão haveria busca dobrada.
     * Precisa ser memoizado no chamador (array novo a cada render re-semearia).
     */
    initialFilters?: Omit<AppliedFilter, 'id'>[];
}

let filterId = 0;
const nextId = () => `f_${++filterId}`;

const FilterBuilder: React.FC<Props> = ({
    schema, onSearch, loading, className,
    collapsible = true, defaultCollapsed = false, title = 'Filtros',
    initialFilters,
}) => {
    const { current, push, undo, redo, reset, canUndo, canRedo } = useFilterHistory();
    const [filters, setFilters] = useState<AppliedFilter[]>([]);
    const [showAddMenu, setShowAddMenu] = useState(false);
    const [collapsed, setCollapsed] = useState(collapsible && defaultCollapsed);

    // Sync with history
    useEffect(() => { setFilters(current); }, [current]);

    // Initialize with visible fields (no value = not applied yet), já aplicando o que veio de fora
    useEffect(() => {
        const vindoDeFora = (key: string) => initialFilters?.find(f => f.key === key);
        const visiveis: AppliedFilter[] = schema
            .filter(s => s.visible)
            .map(s => {
                const externo = vindoDeFora(s.key);
                return {
                    id: nextId(),
                    key: s.key,
                    label: s.label,
                    type: s.type,
                    operator: externo?.operator ?? s.defaultOperator,
                    value: externo ? externo.value : (s.type === 'boolean' ? null : ''),
                };
            });
        // deep-link em campo oculto entra como chip extra, senão o filtro valeria sem aparecer
        const extras: AppliedFilter[] = (initialFilters || [])
            .filter(f => !visiveis.some(v => v.key === f.key))
            .map(f => ({ ...f, id: nextId() }));
        const todos = [...visiveis, ...extras];
        setFilters(todos);
        // registra no histórico para o resumo do painel colapsado mostrar o que está valendo
        const aplicados = todos.filter(f => f.value !== '' && f.value !== null && f.value !== undefined);
        if (aplicados.length) push(aplicados);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schema, initialFilters]);

    const addFilter = (field: FilterSchema) => {
        const newFilter: AppliedFilter = {
            id: nextId(),
            key: field.key,
            label: field.label,
            type: field.type,
            operator: field.defaultOperator,
            value: field.type === 'boolean' ? null : '',
        };
        setFilters(prev => [...prev, newFilter]);
        setShowAddMenu(false);
    };

    const updateFilter = (id: string, updated: AppliedFilter) => {
        setFilters(prev => prev.map(f => f.id === id ? updated : f));
    };

    const removeFilter = (id: string) => {
        setFilters(prev => prev.filter(f => f.id !== id));
    };

    const handleSearch = useCallback(() => {
        // Only include filters with values
        const active = filters.filter(f => {
            if (f.type === 'boolean') return true; // boolean always applies
            if (Array.isArray(f.value)) return f.value.some(v => v !== '');
            return f.value !== '' && f.value !== null && f.value !== undefined;
        });
        push(active);
        onSearch(active);
    }, [filters, push, onSearch]);

    const handleReset = () => {
        reset();
        const initial = schema
            .filter(s => s.visible)
            .map(s => ({
                id: nextId(),
                key: s.key,
                label: s.label,
                type: s.type,
                operator: s.defaultOperator,
                value: s.type === 'boolean' ? null : '',
            }));
        setFilters(initial);
        onSearch([]);
    };

    const handleUndo = () => { undo(); };
    const handleRedo = () => { redo(); };

    // Keyboard shortcuts
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) { e.preventDefault(); handleUndo(); }
            if ((e.metaKey || e.ctrlKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) { e.preventDefault(); handleRedo(); }
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); handleSearch(); }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [handleSearch]);

    // Campos disponíveis para adicionar: qualquer um do schema que não esteja na tela.
    // (inclui os `visible` que o usuário removeu — antes eles nunca voltavam ao menu)
    const availableFields = schema.filter(s => !filters.find(f => f.key === s.key));

    // Resumo do que está aplicado (última busca), mostrado quando o painel está colapsado.
    const appliedSummary = useMemo(
        () => current.map(f => describeFilter(f, operatorLabels[f.operator])),
        [current]
    );

    return (
        <div className={`bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-lg p-3 mb-1 relative overflow-visible ${className || ''}`}>
            {/* Cabeçalho (colapsar) */}
            {collapsible && (
                <button onClick={() => setCollapsed(v => !v)}
                    className={`w-full flex items-center gap-2 text-xs font-semibold text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white ${collapsed ? '' : 'mb-2 pb-2 border-b dark:border-slate-700'}`}>
                    <FaFilter size={10} className="text-gray-400 shrink-0" />
                    <span className="shrink-0">{title}</span>
                    {appliedSummary.length > 0 && (
                        <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200 text-[10px] shrink-0">
                            {appliedSummary.length} aplicado{appliedSummary.length > 1 ? 's' : ''}
                        </span>
                    )}
                    {collapsed && appliedSummary.length > 0 && (
                        <span className="truncate font-normal text-gray-400 dark:text-gray-500" title={appliedSummary.join(' · ')}>
                            {appliedSummary.join(' · ')}
                        </span>
                    )}
                    <span className="flex-1" />
                    {collapsed ? <FaChevronDown size={11} className="shrink-0" /> : <FaChevronUp size={11} className="shrink-0" />}
                </button>
            )}

            {!collapsed && (
                <>
                    {/* Active filters */}
                    <div className="space-y-1.5">
                        {filters.map(filter => {
                            const fieldSchema = schema.find(s => s.key === filter.key);
                            if (!fieldSchema) return null;
                            return (
                                <FilterField
                                    key={filter.id}
                                    filter={filter}
                                    schema={fieldSchema}
                                    onChange={updated => updateFilter(filter.id, updated)}
                                    onRemove={() => removeFilter(filter.id)}
                                />
                            );
                        })}
                    </div>

                    {/* Actions bar */}
                    <div className="flex items-center gap-2 mt-3 pt-2 border-t dark:border-slate-700">
                        {/* Add filter */}
                        <div className="relative">
                            <button onClick={() => setShowAddMenu(!showAddMenu)}
                                className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-slate-700 rounded">
                                <FaPlus size={9} /> Filtro
                            </button>
                            {showAddMenu && availableFields.length > 0 && (
                                <div className="absolute top-full left-0 mt-1 bg-white dark:bg-slate-800 border dark:border-slate-600 rounded shadow-lg z-50 py-1 min-w-[200px] max-h-[250px] overflow-y-auto">
                                    {availableFields.map(field => (
                                        <button key={field.key} onClick={() => addFilter(field)}
                                            className="block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-300">
                                            {field.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Undo / Redo */}
                        <button onClick={handleUndo} disabled={!canUndo} title="Desfazer (Ctrl+Z)"
                            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed">
                            <FaUndo size={11} />
                        </button>
                        <button onClick={handleRedo} disabled={!canRedo} title="Refazer (Ctrl+Y)"
                            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed">
                            <FaRedo size={11} />
                        </button>

                        <div className="flex-1" />

                        {/* Reset */}
                        <button onClick={handleReset}
                            className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-red-500">
                            <FaTimes size={9} /> Limpar
                        </button>

                        {/* Search */}
                        <button onClick={handleSearch} disabled={loading}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50">
                            <FaSearch size={10} /> {loading ? 'Buscando...' : 'Buscar'}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default FilterBuilder;
